import React from 'react';
import { observer } from 'mobx-react-lite';
import { DbRecordingsTableWriter } from '../../State/EvidenceDB/DbRecordingsTableWriter';
import { DbRecordingEntryWriter } from '../../State/EvidenceDB/DbRecordingEntryWriter';
import { styled } from 'styled-components';
import { friendlyTimeSince } from '../../lib/utils/friendlyTimeSince';
import { DbRecording, isISODateTimeString, UUID } from '../../State/EvidenceDB/DatabaseTypes';
import { HeaderTitleHeight, RecordingsPageHeaderView } from './HeaderView';
import { BorderlessCancelButton } from './common-styles';
import { WindowRoutingState } from './WindowRoutingState';

const TableContainerElement = styled.div`
	box-sizing: border-box;
	display: flex;
	flex-direction: column;
	justify-content: start;
	align-items: center;
	width: 100vw;
	padding-left: 2.5vw;
	padding-right: 2.5vw;
`;

const RecordingsTableElement = styled.table`
  border-spacing: 0;

	& th, & td {
  	padding-left: 1rem;
		padding-right: 1rem;
	}

	& thead {
		border-bottom: 1px solid black;
		background-color: rgba(128, 128, 128, 0.1);
	}

	& thead th:nth-child(1), & thead th:nth-child(2), & thead th:nth-child(3) {
		text-align: start;	
	}

	& thead th:nth-child(4), & tbody td:nth-child(4),
	& thead th:nth-child(5), & tbody td:nth-child(5) {
		text-align: right;	
	}

	& tbody tr:hover {
		background-color: rgba(253, 249, 3, 0.5);
		cursor: pointer;
	}

	& input {
		width: calc(min(40vw, 24rem));
	}
`;


export const RecordingRowView = observer( ({recordingWriter, windowRoutingState}:
	{recordingWriter: DbRecordingEntryWriter, windowRoutingState: WindowRoutingState}
) => {

	// const updateName = ({recordingWriter}: {recordingWriter: DbRecordingEntryWriter}) => 
	// 	async (e: React.ChangeEvent<HTMLInputElement>) => {
	// 		const name = e.currentTarget.value.length > 0 ? e.currentTarget.value : undefined;
	// 		await recordingWriter.setName(name);
	// 	}
	const {dataSummary} = recordingWriter.recording;

	let websitesListAsString = dataSummary?.sourcePageRegisteredDomains
		?.toSorted( (a, b) => b.numberOfRequests - a.numberOfRequests)
		?.map( ({registeredDomain}) => registeredDomain )
		?.join(", ") ?? "";	
	if (websitesListAsString.length >= 40) {
		websitesListAsString = websitesListAsString.slice(0, 39) + "…";
	}
	
	const whenLastObservedIso = dataSummary?.whenLastObservedIso;
	const whenFirstObservedIso = dataSummary?.whenFirstObservedIso;
	const finishedAt = isISODateTimeString(whenLastObservedIso) ? friendlyTimeSince(whenLastObservedIso) : (<i>in progress</i>)
	const durationInSeconds = !isISODateTimeString(whenFirstObservedIso) || !isISODateTimeString(whenLastObservedIso) ? undefined :
		Math.round( (new Date(whenLastObservedIso).getTime() - new Date(whenFirstObservedIso).getTime()) / 1000);
	const durationString = durationInSeconds == null ? undefined : 
		durationInSeconds < 3600 ? `${Math.floor(durationInSeconds / 60)}:${(durationInSeconds % 60).toLocaleString('en', {minimumIntegerDigits: 2})}` :
		`${Math.floor(durationInSeconds / 3600)}:${Math.floor((durationInSeconds % 3600) / 60)}:${(durationInSeconds % 60).toLocaleString('en', {minimumIntegerDigits: 2})}`;
	
  const deleteRecording = (dbRecordingEntryWriter: DbRecordingEntryWriter) => async (event: React.MouseEvent) => {
		event.stopPropagation();
		if(window.confirm(`Delete recording ${dbRecordingEntryWriter.nameOrDescription}`)) {
			await windowRoutingState.recordingsTableWriter.deleteRecording(dbRecordingEntryWriter.recording.recordingUuid);
		}
	}

	return (
		<tr key={recordingWriter.recording.recordingUuid} onClick={ () => windowRoutingState.pushPage(recordingWriter.recording.recordingUuid) } >
			{/* FIXME -- need to preserve cursor on change, see https://stackoverflow.com/questions/46000544/react-controlled-input-cursor-jumps */}
			<td>{ // <input type="text" defaultValue={recordingWriter.name} placeholder={recordingWriter.placeholderName} onChange={updateName({recordingWriter})} />
				recordingWriter.name ?? (<i>unnamed</i>)
			}</td>
			<td>{websitesListAsString}</td>
			<td>{finishedAt}</td>
			<td>{durationString}</td>
			<td>{dataSummary?.numberOfRequests}</td>
			<td><BorderlessCancelButton onClick={deleteRecording(recordingWriter)}>✗</BorderlessCancelButton></td>
		</tr>
		)
	});

// Name, start time, domains, number of events, number of requests
const RecordingsPageContainer = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
`;


export const RecordingsView = observer ( ({windowRoutingState}: {windowRoutingState: WindowRoutingState}) => {
	const recordingWriters = windowRoutingState.recordingsTableWriter.recordingWritersNewestToOldest;
	return (<>
		<RecordingsPageHeaderView {...{windowRoutingState}} />
		<RecordingsPageContainer>
			<TableContainerElement>
				<h2>Recordings</h2>
				<RecordingsTableElement>
					<thead>
						<tr>
							<th>Name</th>
							<th>Website(s)</th>
							<th>Finished</th>
							<th>Duration</th>
							<th>Requests</th>
							<th></th>
						</tr>
					</thead>
					{/* <div>{ JSON.stringify(recordingWriters) }</div> */}
					<tbody>
					{
						recordingWriters.map( (recordingWriter) => (<RecordingRowView key={recordingWriter.recordingUuid} {...{recordingWriter, windowRoutingState}} />) )
					}
				</tbody>
				</RecordingsTableElement>
			</TableContainerElement>
		</RecordingsPageContainer></>
	)});
